import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { DEFAULT_EXPIRE_DAYS } from 'constants/cookies';

type StorageStrategy = 'cookie' | 'localStorage' | 'sessionStorage' | 'memory';

interface DismissalStorage {
  get: (key: string) => string | null;
  set: (key: string, value: string) => void;
}

const createStorage = (strategy: StorageStrategy): DismissalStorage => {
  switch (strategy) {
    case 'cookie':
      return {
        get: key => Cookies.get(key) || null,
        set: (key, value) => Cookies.set(key, value, { expires: DEFAULT_EXPIRE_DAYS }),
      };
    case 'localStorage':
      return {
        get: key => localStorage.getItem(key),
        set: (key, value) => localStorage.setItem(key, value),
      };
    case 'sessionStorage':
      return {
        get: key => sessionStorage.getItem(key),
        set: (key, value) => sessionStorage.setItem(key, value),
      };
    case 'memory': {
      const memoryStore = new Map<string, string>();
      return {
        get: key => memoryStore.get(key) || null,
        set: (key, value) => memoryStore.set(key, value),
      };
    }
    default:
      throw new Error(`Unsupported storage strategy: ${strategy}`);
  }
};

interface UseBannerDismissalOptions {
  storageKey: string;
  storageStrategy?: StorageStrategy;
  dismissalDurationMs: number;
  persistDismissal?: boolean;
}

interface UseBannerDismissalReturn {
  isDismissed: boolean;
  isLoading: boolean;
  dismiss: () => void;
  reset: () => void;
}

export const useBannerDismissal = ({
  storageKey,
  storageStrategy = 'cookie',
  dismissalDurationMs,
  persistDismissal = true,
}: UseBannerDismissalOptions): UseBannerDismissalReturn => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const storage = createStorage(storageStrategy);

  const storeDismissal = (): void => {
    if (!persistDismissal) return;

    try {
      storage.set(storageKey, Date.now().toString());
    } catch (error) {
      console.warn('Failed to store dismissal:', error);
    }
  };

  const dismiss = (): void => {
    storeDismissal();
    setIsDismissed(true);
  };

  const reset = (): void => {
    try {
      if (storageStrategy === 'cookie') {
        Cookies.remove(storageKey);
      } else if (storageStrategy === 'localStorage') {
        localStorage.removeItem(storageKey);
      } else if (storageStrategy === 'sessionStorage') {
        sessionStorage.removeItem(storageKey);
      }
      setIsDismissed(false);
    } catch (error) {
      console.warn('Failed to reset dismissal:', error);
    }
  };

  useEffect(() => {
    const checkDismissal = (): boolean => {
      if (!persistDismissal) return false;

      try {
        const dismissedData = storage.get(storageKey);
        if (dismissedData) {
          const dismissedTimestamp = parseInt(dismissedData, 10);
          const timeElapsed = Date.now() - dismissedTimestamp;
          return timeElapsed < dismissalDurationMs;
        }
        return false;
      } catch (error) {
        console.warn('Failed to check dismissal:', error);
        return false;
      }
    };

    setIsDismissed(checkDismissal());
    setIsLoading(false);
  }, [storageKey, storageStrategy, dismissalDurationMs, persistDismissal, storage]);

  return {
    isDismissed,
    isLoading,
    dismiss,
    reset,
  };
}; 